import {useNavigate} from 'react-router-dom';
import {useLayoutEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {InfoIcon} from 'lucide-react';
import {PATHS} from 'routes/constants';
import {Alert, Button, cn, useModalControls} from '@chekin/guestapp-ui-kit';
import {useDispenserControls, useKioskoStore} from 'context/kiosko';
import {useBuildNavigationItems, useGuestGroup, usePath} from 'hooks';
import keycardIcon from 'assets/icons/key-card.svg';
import InternalRemoteAccess from '../InternalRemoteAccess';
import {ProgressTaskBar} from 'components/Kiosko/ProgressTaskBar';
import {PrintingCardsModal} from 'components/Kiosko/PrintingCardsModal';
import {RegisteredGuestsInfoModal} from 'components/Kiosko/RegisteredGuestsInfoModal';
import {Loader} from '@guestapp/old-ui-kit';

function RemoteKeys() {
  const {t} = useTranslation();
  const {guestGroup, isLoading} = useGuestGroup();
  const printed = useKioskoStore(state => state.cards.printed);
  const pending = useKioskoStore(state => state.cards.pending);
  const pendingGuests =
    (guestGroup?.known_number_of_guests ?? 0) - (guestGroup?.added_number_of_guests ?? 0);
  const {getFullPath} = usePath();
  const {printCard} = useDispenserControls();
  const {getRegistrationPath} = useBuildNavigationItems();
  const {
    isOpen: isOpenRegisteredGuestsInfoModal,
    closeModal: closeRegisteredGuestsInfoModal,
    openModal: openRegisteredGuestsInfoModal,
  } = useModalControls();

  const navigate = useNavigate();
  useLayoutEffect(() => {
    openRegisteredGuestsInfoModal();
  }, [openRegisteredGuestsInfoModal]);

  if (isLoading) {
    return <Loader />;
  }

  return (
    <InternalRemoteAccess isLoading={isLoading}>
      <div className="flex flex-col gap-4">
        <div className="not-italic font-semibold leading-[56px] text-kiosko-m text-center">
          {t('my_key_cards')}
        </div>
        <div className="flex flex-col gap-14">
          <div className="text-kiosko-s text-center">
            {t('each_keycard_provides_access_to_your_assigned_areas')}
          </div>
          <div className="flex flex-col justify-center items-center gap-9 self-stretch p-14 rounded-[38px] bg-white">
            <ProgressTaskBar
              completed={printed}
              title={t('printed_keycards')}
              icon={<img src={keycardIcon} alt="printed" className="w-14 h-14" />}
              total={guestGroup?.known_number_of_guests ?? 0}
            />
            <div className="w-full text-kiosko-s">
              {t('keycard_printed_and_remaining', {
                printed,
                remaining: pending ?? guestGroup?.known_number_of_guests,
              })}
            </div>
          </div>

          <Alert
            className={cn(
              'text-[var(--primary)] [--lucide-stroke-width:3] max-w-[400px] text-left flex gap-3 mb-6',
              'max-w-full p-[18px_38px_18px_18px] !text-kiosko-s leading-[56px]',
              'rounded-[24px]',
            )}
          >
            <div className="kiosko:p-1">
              <InfoIcon className="h-5 w-5 kiosko:h-10 kiosko:w-10" />
            </div>
            <span className="kiosko:pl-6">
              {t('make_sure_the_document_properly_lies_flat')}
            </span>
          </Alert>

          {pending > 0 && (
            <Button onClick={() => printCard()} size="md" className="m-auto">
              {t('print_new_keycard')}
            </Button>
          )}
          <PrintingCardsModal />
          <RegisteredGuestsInfoModal
            isOpen={isOpenRegisteredGuestsInfoModal}
            registeredGuests={guestGroup?.added_number_of_guests}
            onRegisterNext={() => {
              navigate(getFullPath(getRegistrationPath()));
            }}
            onGoHome={() => {
              navigate(getFullPath(PATHS.home));
            }}
            onClose={closeRegisteredGuestsInfoModal}
            pendingGuests={pendingGuests}
          />
        </div>
      </div>
    </InternalRemoteAccess>
  );
}

export {RemoteKeys};
